import { useState, type FormEvent, type JSX } from 'react'
import { api } from '../../api/client.js'

interface Props {
  passwordChangeToken: string
  /** The temporary password just typed at sign-in; sent as proof of the current password. */
  currentPassword: string
  onDone: () => void
  onClose: () => void
}

const inputClass =
  'w-full px-4 py-3 pr-12 border border-gray-300 rounded-[3px] focus:outline-none focus:ring-2 focus:ring-[#3366FF] focus:border-[#3366FF] transition-all text-gray-900 placeholder-gray-400 disabled:bg-gray-50'

function EyeIcon({ off }: { off: boolean }): JSX.Element {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12Z" />
      <circle cx="12" cy="12" r="3" />
      {off && <path d="M4 4l16 16" />}
    </svg>
  )
}

/** SE-07: shown instead of a session when the account's password is temporary. */
export default function ForcedPasswordChangeDialog({ passwordChangeToken, currentPassword, onDone, onClose }: Props): JSX.Element {
  const [newPassword, setNewPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [show, setShow] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const mismatch = confirm !== '' && newPassword !== confirm
  const matches = confirm !== '' && newPassword === confirm

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault()
    setError(null)
    if (newPassword !== confirm) {
      setError('The two passwords do not match')
      return
    }
    setBusy(true)
    try {
      await api.post('/auth/password/change-required', { passwordChangeToken, currentPassword, newPassword })
      onDone()
    } catch (err) {
      const e2 = err as { body?: { message?: string }; message?: string }
      setError(e2.body?.message ?? e2.message ?? 'Could not change the password')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0B1120]/70 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="forced-pw-title">
      <form onSubmit={(e) => void submit(e)} className="w-full max-w-md overflow-hidden rounded-[6px] bg-white shadow-2xl">
        <div className="h-[3px] bg-gradient-to-r from-[#3366FF] to-[#FF9933]" />
        <div className="space-y-5 p-6 sm:p-8">
          <div className="flex items-start gap-4">
            <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-[3px] bg-[#3366FF]/10 text-[#3366FF]">
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="4" y="10" width="16" height="10" rx="2" />
                <path d="M8 10V7a4 4 0 0 1 8 0v3" />
              </svg>
            </div>
            <div>
              <h2 id="forced-pw-title" className="text-xl font-semibold text-gray-900">Choose a new password</h2>
              <p className="mt-1 text-sm text-gray-600">Your password was set by an administrator. Choose your own before you continue.</p>
            </div>
          </div>

          <div>
            <label htmlFor="forced-pw-new" className="mb-1.5 block text-sm font-medium text-gray-700">New password</label>
            <div className="relative">
              <input id="forced-pw-new" type={show ? 'text' : 'password'} autoComplete="new-password" autoFocus value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)} disabled={busy} className={inputClass} />
              <button type="button" onClick={() => setShow((s) => !s)} aria-label={show ? 'Hide passwords' : 'Show passwords'} aria-pressed={show}
                className="absolute inset-y-0 right-0 flex w-12 items-center justify-center text-gray-400 hover:text-gray-600 focus:outline-none focus-visible:text-[#3366FF]">
                <EyeIcon off={show} />
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="forced-pw-confirm" className="mb-1.5 block text-sm font-medium text-gray-700">Confirm new password</label>
            <input id="forced-pw-confirm" type={show ? 'text' : 'password'} autoComplete="new-password" value={confirm}
              onChange={(e) => setConfirm(e.target.value)} disabled={busy} aria-invalid={mismatch} className={inputClass} />
            <p className={`mt-1.5 min-h-[1.25rem] text-xs ${mismatch ? 'text-[#CC5500]' : matches ? 'text-green-700' : 'text-gray-500'}`} aria-live="polite">
              {mismatch ? 'Passwords do not match yet' : matches ? 'Passwords match' : 'Your administrator’s password policy applies.'}
            </p>
          </div>

          {error && (
            <div role="alert" className="rounded-[3px] border-l-[3px] border-[#FF9933] bg-red-50 p-3 text-sm text-red-700">{error}</div>
          )}

          <div className="flex items-center justify-end gap-3 pt-1">
            <button type="button" onClick={onClose} disabled={busy}
              className="rounded-[3px] px-4 py-2.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300 disabled:opacity-50">
              Cancel
            </button>
            <button type="submit" disabled={busy || newPassword === ''}
              className="rounded-[3px] bg-[#3366FF] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-[#2952CC] focus:outline-none focus:ring-2 focus:ring-[#3366FF] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50">
              {busy ? 'Saving…' : 'Change password'}
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}
